/**
 * Lightweight highlight for search snippets.
 * Matches case-insensitively and allows optional Gujarati/Devanagari
 * anusvara marks so પસદ still highlights પસંદ.
 */
import React from 'react';

function escapeRegexChar(ch) {
  return ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Build a pattern that tolerates optional Indic nasal/visarga between letters. */
export function flexibleTokenPattern(token) {
  const chars = Array.from(String(token));
  if (!chars.length) return '';
  return chars
    .map((ch) => escapeRegexChar(ch))
    .join('[\\u0A81-\\u0A83\\u0901-\\u0903\\u064B-\\u0652]?');
}

function buildHighlightRegex(query) {
  const raw = String(query || '').trim();
  if (!raw) return null;
  const tokens = raw
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  if (!tokens.length) return null;
  const patterns = tokens.map(flexibleTokenPattern).filter(Boolean);
  if (!patterns.length) return null;
  return new RegExp(`(${patterns.join('|')})`, 'gi');
}

const MARK_CLASS =
  'bg-teal-200 text-teal-950 dark:bg-teal-700 dark:text-teal-50 rounded px-0.5 not-italic';

export function highlightSnippet(text, query) {
  if (!text) return '';
  const re = buildHighlightRegex(query);
  if (!re) return text;

  const parts = String(text).split(re);
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <mark key={index} className={MARK_CLASS}>
        {part}
      </mark>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  );
}

/**
 * Highlight plain/HTML strings for dangerouslySetInnerHTML.
 * Only mutates text nodes (segments outside of tags).
 */
export function highlightHtml(html, query) {
  if (!html) return '';
  const re = buildHighlightRegex(query);
  if (!re) return String(html);

  const markOpen = `<mark class="${MARK_CLASS}">`;
  return String(html)
    .split(/(<[^>]+>)/g)
    .map((part) => {
      if (!part || part.startsWith('<')) return part;
      return part.replace(re, `${markOpen}$1</mark>`);
    })
    .join('');
}

/** Surah deep-link that preserves the search term for on-page highlight. */
export function buildSurahHighlightPath(surahId, ayahId, query) {
  const q = String(query || '').trim();
  const hl = q ? `?hl=${encodeURIComponent(q)}` : '';
  return `/surah/${surahId}${hl}#ayah-${ayahId}`;
}
